import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  TruckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import { Vehiculo, vehiculosService } from '../../services/vehiculosService';
import { Conductor } from '../../services/conductoresService';
import { useAuth } from '../../contextos/autenticacion/ContextoAutenticacion';

interface ModalEditarVehiculoProps {
  isOpen: boolean;
  onClose: () => void;
  vehiculo: Vehiculo | null;
  onSave: (vehiculo: Vehiculo) => void;
}

const ModalEditarVehiculo: React.FC<ModalEditarVehiculoProps> = ({
  isOpen,
  onClose,
  vehiculo,
  onSave
}) => {
  const { usuario } = useAuth();
  const modoCrear = !vehiculo;

  // Estados del formulario
  const [formData, setFormData] = useState({
    placa: '',
    marca: '',
    modelo: '',
    año: new Date().getFullYear(),
    tipo: 'camion',
    estado: 'disponible',
    conductorId: '',
    capacidadCarga: 0,
    consumoCombustible: 0,
    fechaMantenimiento: '',
    direccion: '',
    vin: ''
  });

  // Estados de control
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState<{ [key: string]: string }>({});
  const [conductoresDisponibles] = useState<Conductor[]>([
    {
      id: '1',
      idOrganizacion: 0,
      numeroLicencia: 'C2-123456',
      tipoLicencia: 'C2',
      vencimientoLicencia: '2025-01-01',
      estado: 'activo',
      idUsuario: '1'
    }
  ]);

  // Cargar datos del vehículo cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      if (vehiculo) {
        setFormData({
          placa: vehiculo.placa,
          marca: vehiculo.marca,
          modelo: vehiculo.modelo,
          año: vehiculo.año,
          tipo: vehiculo.tipo,
          estado: vehiculo.estado,
          conductorId: '',
          capacidadCarga: vehiculo.capacidadCarga,
          consumoCombustible: 0,
          fechaMantenimiento: '',
          direccion: '',
          vin: (vehiculo as any).vin || ''
        });
      } else {
        const hoy = new Date();
        const seisMeses = new Date(hoy.setMonth(hoy.getMonth() + 6));
        setFormData({
          placa: '',
          marca: '',
          modelo: '',
          año: new Date().getFullYear(),
          tipo: 'camion',
          estado: 'disponible',
          conductorId: '',
          capacidadCarga: 0,
          consumoCombustible: 10,
          fechaMantenimiento: seisMeses.toISOString().split('T')[0],
          direccion: '',
          vin: ''
        });
      }
      setErrores({});
    }
  }, [vehiculo, isOpen]);

  // Validaciones
  const validarFormulario = (): boolean => {
    const nuevosErrores: { [key: string]: string } = {};

    if (!formData.placa.trim()) {
      nuevosErrores.placa = 'La placa es requerida';
    }

    if (!formData.marca.trim()) {
      nuevosErrores.marca = 'La marca es requerida';
    }

    if (!formData.modelo.trim()) {
      nuevosErrores.modelo = 'El modelo es requerido';
    }

    if (formData.año < 1990 || formData.año > new Date().getFullYear() + 1) {
      nuevosErrores.año = 'Año inválido';
    }

    if (formData.capacidadCarga <= 0) {
      nuevosErrores.capacidadCarga = 'La capacidad de carga debe ser mayor a 0';
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Manejar cambios en el formulario
  const manejarCambio = (campo: string, valor: any) => {
    setFormData(prev => ({
      ...prev,
      [campo]: valor
    }));

    if (errores[campo]) {
      setErrores(prev => ({
        ...prev,
        [campo]: ''
      }));
    }
  };

  // Guardar cambios
  const manejarGuardar = async () => {
    if (!validarFormulario()) return;

    setGuardando(true);

    try {
      if (modoCrear) {
        const idOrg = typeof usuario?.id_organizacion === 'number' ? usuario.id_organizacion :
                      (typeof usuario?.id_organizacion === 'string' ? parseInt(usuario.id_organizacion, 10) : 0);
        const vehiculoNuevo = await vehiculosService.crearVehiculo({
          idOrganizacion: idOrg,
          placa: formData.placa.toUpperCase(),
          marca: formData.marca,
          modelo: formData.modelo,
          anio: formData.año,
          tipo: formData.tipo,
          estado: formData.estado,
          capacidadCargaKg: formData.capacidadCarga,
          vin: formData.vin || undefined,
        } as any);
        onSave(vehiculoNuevo);
        onClose();
      } else {
        const vehiculoActualizado: Vehiculo = {
          ...vehiculo!,
          placa: formData.placa.toUpperCase(),
          marca: formData.marca,
          modelo: formData.modelo,
          año: formData.año,
          tipo: formData.tipo,
          estado: formData.estado,
          capacidadCarga: formData.capacidadCarga,
          fechaActualizacion: new Date().toISOString()
        };
        try {
          await vehiculosService.actualizarVehiculo(vehiculoActualizado.id, vehiculoActualizado as any);
        } catch {
          // guarda local aunque falle API
        }
        onSave(vehiculoActualizado);
        onClose();
      }

    } catch (error) {
      console.error('Error al guardar:', error);
      setErrores({ general: 'Error al guardar los cambios. Intenta nuevamente.' });
    } finally {
      setGuardando(false);
    }
  };

  // Manejar cierre del modal
  const manejarCerrar = () => {
    if (!guardando) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={manejarCerrar} size="lg" title={modoCrear ? 'Crear Vehículo' : 'Editar Vehículo'}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <TruckIcon className="h-6 w-6 mr-2 text-blue-600" />
            {modoCrear ? 'Crear Vehículo' : 'Editar Vehículo'}
          </h2>
          <button
            onClick={manejarCerrar}
            disabled={guardando}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Error general */}
        {errores.general && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700 dark:text-red-300">{errores.general}</span>
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={(e) => { e.preventDefault(); manejarGuardar(); }} className="space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="vehiculo-placa" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Placa *
              </label>
              <input
                id="vehiculo-placa"
                name="placa"
                type="text"
                value={formData.placa}
                onChange={(e) => manejarCambio('placa', e.target.value.toUpperCase())}
                placeholder="ABC-123"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white ${
                  errores.placa 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={guardando}
                aria-invalid={!!errores.placa}
                aria-describedby={errores.placa ? 'vehiculo-placa-error' : undefined}
              />
              {errores.placa && (
                <p id="vehiculo-placa-error" role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{errores.placa}</p>
              )}
            </div>

            <div>
              <label htmlFor="vehiculo-tipo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Vehículo *
              </label>
              <select
                id="vehiculo-tipo"
                name="tipo"
                value={formData.tipo}
                onChange={(e) => manejarCambio('tipo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                disabled={guardando}
              >
                <option value="camion">Camión</option>
                <option value="camioneta">Camioneta</option>
                <option value="furgon">Furgón</option>
                <option value="motocicleta">Motocicleta</option>
              </select>
            </div>

            <div>
              <label htmlFor="vehiculo-marca" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Marca *
              </label>
              <input
                id="vehiculo-marca"
                name="marca"
                type="text"
                value={formData.marca}
                onChange={(e) => manejarCambio('marca', e.target.value)}
                placeholder="Chevrolet"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white ${
                  errores.marca 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={guardando}
                aria-invalid={!!errores.marca}
                aria-describedby={errores.marca ? 'vehiculo-marca-error' : undefined}
              />
              {errores.marca && (
                <p id="vehiculo-marca-error" role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{errores.marca}</p>
              )}
            </div>

            <div>
              <label htmlFor="vehiculo-modelo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Modelo *
              </label>
              <input
                id="vehiculo-modelo"
                name="modelo"
                type="text"
                value={formData.modelo}
                onChange={(e) => manejarCambio('modelo', e.target.value)}
                placeholder="NPR"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white ${
                  errores.modelo 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={guardando}
                aria-invalid={!!errores.modelo}
                aria-describedby={errores.modelo ? 'vehiculo-modelo-error' : undefined}
              />
              {errores.modelo && (
                <p id="vehiculo-modelo-error" role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{errores.modelo}</p>
              )}
            </div>

            <div>
              <label htmlFor="vehiculo-anio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Año *
              </label>
              <input
                id="vehiculo-anio"
                name="anio"
                type="number"
                value={formData.año}
                onChange={(e) => manejarCambio('año', parseInt(e.target.value))}
                min="1990"
                max={new Date().getFullYear() + 1}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white ${
                  errores.año 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={guardando}
                aria-invalid={!!errores.año}
                aria-describedby={errores.año ? 'vehiculo-anio-error' : undefined}
              />
              {errores.año && (
                <p id="vehiculo-anio-error" role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{errores.año}</p>
              )}
            </div>

            <div>
              <label htmlFor="vehiculo-vin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                VIN (Chasis)
              </label>
              <input
                id="vehiculo-vin"
                name="vin"
                type="text"
                value={formData.vin}
                onChange={(e) => manejarCambio('vin', e.target.value)}
                placeholder="1HGCM82633A004352"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                disabled={guardando}
              />
            </div>

            <div>
              <label htmlFor="vehiculo-estado" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estado
              </label>
              <select
                id="vehiculo-estado"
                name="estado"
                value={formData.estado}
                onChange={(e) => manejarCambio('estado', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                disabled={guardando}
              >
                <option value="disponible">Disponible</option>
                <option value="en_ruta">En Ruta</option>
                <option value="mantenimiento">Mantenimiento</option>
                <option value="fuera_servicio">Fuera de Servicio</option>
              </select>
            </div>
          </div>

          {/* Especificaciones técnicas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="vehiculo-capacidad" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Capacidad de Carga (kg) *
              </label>
              <input
                id="vehiculo-capacidad"
                name="capacidadCarga"
                type="number"
                value={formData.capacidadCarga}
                onChange={(e) => manejarCambio('capacidadCarga', parseInt(e.target.value) || 0)}
                min="1"
                placeholder="3500"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white ${
                  errores.capacidadCarga 
                    ? 'border-red-300 dark:border-red-600' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={guardando}
                aria-invalid={!!errores.capacidadCarga}
                aria-describedby={errores.capacidadCarga ? 'vehiculo-capacidad-error' : undefined}
              />
              {errores.capacidadCarga && (
                <p id="vehiculo-capacidad-error" role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{errores.capacidadCarga}</p>
              )}
            </div>

            <div>
              <label htmlFor="vehiculo-consumo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Consumo de Combustible (km/galón)
              </label>
              <input
                id="vehiculo-consumo"
                name="consumoCombustible"
                type="number"
                value={formData.consumoCombustible}
                onChange={(e) => manejarCambio('consumoCombustible', parseInt(e.target.value) || 0)}
                min="0"
                placeholder="12"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                disabled={guardando}
              />
            </div>
          </div>

          {/* Asignación y mantenimiento */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="vehiculo-conductor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Conductor Asignado
              </label>
              <select
                id="vehiculo-conductor"
                name="conductorId"
                value={formData.conductorId}
                onChange={(e) => manejarCambio('conductorId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                disabled={guardando}
              >
                <option value="">Sin asignar</option>
                {conductoresDisponibles.map((conductor) => (
                  <option key={conductor.id} value={conductor.id}>
                    ID {conductor.id} - {conductor.numeroLicencia}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="vehiculo-mantenimiento" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Próximo Mantenimiento
              </label>
              <input
                id="vehiculo-mantenimiento"
                name="fechaMantenimiento"
                type="date"
                value={formData.fechaMantenimiento}
                onChange={(e) => manejarCambio('fechaMantenimiento', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                disabled={guardando}
              />
            </div>
          </div>

          {/* Ubicación */}
          <div>
            <label htmlFor="vehiculo-direccion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dirección Actual
            </label>
            <input
              id="vehiculo-direccion"
              name="direccion"
              type="text"
              value={formData.direccion}
              onChange={(e) => manejarCambio('direccion', e.target.value)}
              placeholder="Bogotá, Colombia"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
              disabled={guardando}
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={manejarCerrar}
              disabled={guardando}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={guardando}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center transition-colors disabled:opacity-50"
            >
              {guardando ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : modoCrear ? (
                <>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Crear Vehículo
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ModalEditarVehiculo;
